import { useCallback, useRef, useState } from "react";
import { Upload, X, RotateCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { UploadPurpose } from "@/lib/uploads";
import { ANY_MIME, UPLOAD_LIMITS } from "@/lib/uploads";

type FileUploadProps = {
  purpose: UploadPurpose;
  value: string;
  onChange: (url: string) => void;
  onUploaded?: (info: { url: string; sizeBytes: number }) => void;
  label?: string;
  helper?: string;
  className?: string;
  showPreview?: boolean;
};

type Status =
  | { kind: "idle" }
  | { kind: "uploading"; file: File; progress: number }
  | { kind: "error"; file: File; message: string };

function formatBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export function FileUpload({
  purpose,
  value,
  onChange,
  onUploaded,
  label,
  helper,
  className,
  showPreview = true,
}: FileUploadProps) {
  const limit = UPLOAD_LIMITS[purpose];
  const inputRef = useRef<HTMLInputElement>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [dragActive, setDragActive] = useState(false);

  const allowAnyMime = (limit.mimes as readonly string[]).includes(ANY_MIME);
  const acceptAttr = allowAnyMime ? undefined : limit.mimes.join(",");

  const validate = useCallback(
    (file: File): string | null => {
      if (file.size <= 0 || file.size > limit.maxBytes) {
        return `File must be under ${formatBytes(limit.maxBytes)}`;
      }
      if (purpose === "delivery_code" && file.name.toLowerCase().endsWith(".zip")) {
        const t = file.type;
        if (
          !t ||
          t === "application/octet-stream" ||
          t === "application/zip" ||
          t === "application/x-zip-compressed"
        ) {
          return null;
        }
      }
      if (
        !allowAnyMime &&
        !(limit.mimes as readonly string[]).includes(file.type)
      ) {
        return "Unsupported file type";
      }
      return null;
    },
    [allowAnyMime, limit, purpose],
  );

  const upload = useCallback(
    async (file: File) => {
      const err = validate(file);
      if (err) {
        setStatus({ kind: "error", file, message: err });
        return;
      }
      setStatus({ kind: "uploading", file, progress: 0 });

      const clientReportedType = file.type || "application/octet-stream";
      let presigned: { uploadUrl: string; publicUrl: string; contentType: string };
      try {
        const res = await fetch("/api/uploads/presign", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            purpose,
            contentType: clientReportedType,
            sizeBytes: file.size,
            filename: file.name,
          }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? "Could not start upload");
        }
        presigned = (await res.json()) as {
          uploadUrl: string;
          publicUrl: string;
          contentType: string;
        };
      } catch (e) {
        setStatus({
          kind: "error",
          file,
          message: (e as Error).message || "Could not start upload",
        });
        return;
      }

      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;
      xhr.open("PUT", presigned.uploadUrl);
      xhr.setRequestHeader("Content-Type", presigned.contentType);

      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        const progress = Math.round((evt.loaded / evt.total) * 100);
        setStatus((cur) =>
          cur.kind === "uploading" ? { ...cur, progress } : cur,
        );
      };
      xhr.onerror = () => {
        setStatus({ kind: "error", file, message: "Upload failed" });
        xhrRef.current = null;
      };
      xhr.onabort = () => {
        xhrRef.current = null;
      };
      xhr.onload = () => {
        xhrRef.current = null;
        if (xhr.status >= 200 && xhr.status < 300) {
          setStatus({ kind: "idle" });
          onChange(presigned.publicUrl);
          onUploaded?.({ url: presigned.publicUrl, sizeBytes: file.size });
        } else {
          const hint =
            xhr.status === 403
              ? " (403: check Content-Type vs signed URL)"
              : ` (${xhr.status})`;
          setStatus({
            kind: "error",
            file,
            message: `Upload failed${hint}`,
          });
        }
      };
      xhr.send(file);
    },
    [onChange, onUploaded, purpose, validate],
  );

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    void upload(files[0]!);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  }

  function cancelUpload() {
    xhrRef.current?.abort();
    setStatus({ kind: "idle" });
  }

  function clear() {
    onChange("");
    setStatus({ kind: "idle" });
    if (inputRef.current) inputRef.current.value = "";
  }

  const hasValue = value !== "";
  const isImage =
    hasValue && /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(value);

  return (
    <div className={cn("space-y-2", className)}>
      {label ? (
        <div className="text-sm font-medium">{label}</div>
      ) : null}

      {status.kind === "uploading" ? (
        <div className="rounded-lg border bg-muted/40 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 text-sm">
              <div className="truncate">{status.file.name}</div>
              <div className="text-xs text-muted-foreground">
                {status.progress}% · {formatBytes(status.file.size)}
              </div>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={cancelUpload}>
              Cancel
            </Button>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-[width] duration-150"
              style={{ width: `${status.progress}%` }}
            />
          </div>
        </div>
      ) : hasValue ? (
        <div className="flex items-center gap-3 rounded-lg border p-3">
          {showPreview && isImage ? (
            <img
              src={value}
              alt=""
              className="size-14 rounded object-contain"
            />
          ) : (
            <div className="grid size-14 place-items-center rounded bg-muted text-xs text-muted-foreground">
              File
            </div>
          )}
          <div className="min-w-0 flex-1">
            <a
              href={value}
              target="_blank"
              rel="noreferrer"
              className="block truncate text-sm font-medium underline-offset-4 hover:underline"
            >
              {value.split("/").pop()}
            </a>
            <div className="text-xs text-muted-foreground">Uploaded</div>
          </div>
          <Button type="button" variant="ghost" size="icon-sm" onClick={clear} aria-label="Remove">
            <X className="size-4" />
          </Button>
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={cn(
            "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center transition-colors",
            dragActive ? "border-primary bg-primary/5" : "hover:bg-muted/40",
          )}
        >
          <Upload className="size-5 text-muted-foreground" />
          <div className="text-sm">
            <span className="font-medium">Click to upload</span> or drag and drop
          </div>
          {helper ? (
            <div className="text-xs text-muted-foreground">{helper}</div>
          ) : null}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {status.kind === "error" ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
          <span>{status.message}</span>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() => void upload(status.file)}
          >
            <RotateCw className="size-3" />
            Retry
          </Button>
        </div>
      ) : null}
    </div>
  );
}
